import type { Concept, HospitalityGroup } from "@mmh/types"

export class ApiError extends Error {
    constructor(
        message: string,
        public statusCode?: number,
        public response?: Response
    ) {
        super(message)
        this.name = "ApiError"
    }
}

export function getApiBaseUrl(): string {
    if (import.meta.env.PUBLIC_API_BASE_URL) return import.meta.env.PUBLIC_API_BASE_URL
    // In browser: use same host as the page so http://172.20.10.2:3000 → API at http://172.20.10.2:4000
    if (typeof window !== "undefined") return `http://${window.location.hostname}:4000`
    return "http://localhost:4000"
}

async function fetchWithRetry(
    url: string,
    options: RequestInit = {},
    retries = 3,
    delay = 1000
): Promise<Response> {
    let lastError: Error | null = null

    for (let i = 0; i < retries; i++) {
        try {
            return await fetch(url, options)
        } catch (error) {
            lastError = error instanceof Error ? error : new Error(String(error))
            console.error(`API request failed: ${JSON.stringify(lastError)}`)
        }

        if (i < retries - 1) {
            await new Promise((resolve) => setTimeout(resolve, delay * Math.pow(2, i)))
        }
    }

    throw lastError || new Error("Failed to fetch after retries")
}

export async function fetchConcept(slug: string): Promise<Concept> {
    const apiUrl = getApiBaseUrl()
    const url = `${apiUrl}/config/concepts/${slug}`

    try {
        const response = await fetchWithRetry(url)
        const data = await response.json()
        return data
    } catch (error) {
        console.error(`Failed to fetch concept: ${slug}`, error)
        throw error
    }
}

export async function fetchAllConcepts(): Promise<Concept[]> {
    const apiUrl = getApiBaseUrl()
    const url = `${apiUrl}/config/concepts`

    try {
        const response = await fetchWithRetry(url)
        const data = await response.json()

        return data
    } catch (error) {
        console.error("Failed to fetch concepts", error)
        throw error
    }
}

export async function fetchHospitalityGroup(): Promise<HospitalityGroup> {
    const apiUrl = getApiBaseUrl()
    const url = `${apiUrl}/config/hospitality-group`

    try {
        const response = await fetchWithRetry(url)
        const data = await response.json()

        return data
    } catch (error) {
        console.error("Failed to fetch hospitality group", error)
        throw error
    }
}

export async function loadConceptWithFallback(
    slug: string,
    fallbackConfig: Concept
): Promise<{ concept: Concept; source: "api" | "fallback" }> {
    try {
        const concept = await fetchConcept(slug)
        return { concept, source: "api" }
    } catch (error) {
        console.warn("Failed to fetch concept from API, using fallback config", error)
        return { concept: fallbackConfig, source: "fallback" }
    }
}